/**
 * API Thông báo (contract P3 — controller BE notification/notification.controller.ts).
 *
 * ⚠️ P3 đã thay endpoint cũ (PATCH danh-dau-da-doc / xoa-tat-ca-thong-bao):
 *   - GET  /thong-bao/danh-sach-thong-bao   → {duLieu, tongSo, chuaDoc, page, pageSize}
 *   - PUT  /thong-bao/danh-dau-da-xem       → body {thongBaoId?} (bỏ trống = tất cả)
 *   - DELETE /thong-bao/:id/xoa-thong-bao
 *   - POST /thong-bao/dang-ky-token         → đăng ký FCM token
 *   - PUT  /thong-bao/huy-token
 * Module van-hanh dùng thêm bản đầy đủ typed tại `apis/vanHanhApi.ts` (thongBaoVanHanhApi).
 */
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type {
  CheDoPhamViThongBao,
  CheDoThongBao,
  KenhThongBao,
} from '@/constants/thongBaoConstants'

/** Row thông báo theo contract mới (title/content/loaiThongBao/refId/watched). */
export interface ThongBaoMoi {
  id: string
  title: string
  content: string
  loaiThongBao: string
  refId?: string | null
  watched: boolean
  hanHetHan?: string | null
  ngayTao: string
  laThongBaoChung: boolean
}

export type ThongBaoListResult = PagedListResult<ThongBaoMoi> & { chuaDoc: number }

/** GET /thong-bao/danh-sach-thong-bao — qs: page/pageSize/watched/loaiThongBao/searchQuery. */
export function getThongBaoList(querySt?: string): Promise<ThongBaoListResult> {
  const params = querySt
    ? Object.fromEntries(new URLSearchParams(querySt.replace(/^\?/, '')))
    : {}
  return apiRequest(
    ApisServiceApp.get('/thong-bao/danh-sach-thong-bao', { params }).then((body: unknown) => {
      const outer = (body as { data?: { duLieu?: ThongBaoMoi[]; data?: ThongBaoMoi[] } }).data
      const norm = normalizeListResponse(body as Parameters<typeof normalizeListResponse>[0])
      const rows = Array.isArray(outer?.duLieu) ? outer!.duLieu! : norm.data as ThongBaoMoi[]
      return {
        data: rows.map((row) => ({ ...row, laThongBaoChung: row.laThongBaoChung ?? false })),
        paging: norm.paging,
        chuaDoc: Number((outer as { chuaDoc?: number } | undefined)?.chuaDoc ?? 0),
      }
    }),
  )
}

/** PUT /thong-bao/danh-dau-da-xem — truyền id để đánh dấu 1 thông báo; bỏ trống = tất cả. */
export function markAllThongBaoRead(thongBaoId?: string) {
  return apiRequest(ApisServiceApp.put('/thong-bao/danh-dau-da-xem', thongBaoId ? { thongBaoId } : {}))
}

/** Đánh dấu 1 thông báo đã xem (alias cùng semantics markAllThongBaoRead(id)). */
export function markThongBaoRead(id: string) {
  return markAllThongBaoRead(id)
}

/** DELETE /thong-bao/:id/xoa-thong-bao — xóa mềm. */
export function deleteThongBao(id: string) {
  return apiRequest(ApisServiceApp.delete(`/thong-bao/${id}/xoa-thong-bao`))
}

/** POST /thong-bao — đăng ký FCM device token. */
export function dangKyThongBaoToken(payload: { token: string; deviceType: string; deviceId?: string }) {
  return apiRequest(ApisServiceApp.post('/thong-bao/dang-ky-token', payload))
}

/** PUT /thong-bao/huy-token. */
export function huyThongBaoToken(payload: { token: string }) {
  return apiRequest(ApisServiceApp.put('/thong-bao/huy-token', payload))
}

export interface CaiDatThongBao {
  loaiThongBao: string
  cheDo: CheDoThongBao
  tatDen: string | null
}

/** GET /thong-bao/cai-dat-thong-bao — chỉ các loại tôi có quyền nhận (+ NHAN_APP_KHAC của mobile). */
export function getCaiDatThongBao(): Promise<CaiDatThongBao[]> {
  return apiRequest(
    ApisServiceApp.get('/thong-bao/cai-dat-thong-bao').then((body: unknown) => {
      const data = (body as { data?: unknown }).data
      return Array.isArray(data) ? (data as CaiDatThongBao[]) : []
    }),
  )
}

/** PUT /thong-bao/cap-nhat-cai-dat-thong-bao — tatDen bắt buộc (ISO, tương lai) khi cheDo = TAT_DEN. */
export function capNhatCaiDatThongBao(payload: {
  loaiThongBao: string
  cheDo: CheDoThongBao
  tatDen?: string
}): Promise<CaiDatThongBao> {
  return apiRequest(
    ApisServiceApp.put('/thong-bao/cap-nhat-cai-dat-thong-bao', payload).then(
      (body: unknown) => (body as { data: CaiDatThongBao }).data,
    ),
  )
}

export interface PhamViThongBaoItem {
  duAnId: number
  duAnMa: string
  duAnTen: string
  chiNhanhId: number
  chiNhanhMa: string
  chiNhanhTen: string
  cheDo: CheDoPhamViThongBao
  tatDen: string | null
  dangNhan: boolean
}

export interface PhamViThongBao {
  kenh: KenhThongBao
  daCauHinh: boolean
  items: PhamViThongBaoItem[]
}

/** GET /thong-bao/pham-vi-thong-bao?kenh=WEB|MOBILE */
export function getPhamViThongBao(kenh: KenhThongBao): Promise<PhamViThongBao> {
  return apiRequest(
    ApisServiceApp.get('/thong-bao/pham-vi-thong-bao', { params: { kenh } }).then(
      (body: unknown) =>
        (body as { data: PhamViThongBao }).data ?? {
          kenh,
          daCauHinh: false,
          items: [],
        },
    ),
  )
}

/** PUT /thong-bao/cap-nhat-pham-vi-thong-bao */
export function capNhatPhamViThongBao(payload: {
  kenh: KenhThongBao
  items: Array<{
    duAnId: number
    chiNhanhId: number
    cheDo: CheDoPhamViThongBao
    tatDen?: string
  }>
}): Promise<PhamViThongBao> {
  return apiRequest(
    ApisServiceApp.put('/thong-bao/cap-nhat-pham-vi-thong-bao', payload).then(
      (body: unknown) => (body as { data: PhamViThongBao }).data,
    ),
  )
}
